<template>
  <div class="md-layout">
    <md-card class="md-layout-item md-size-50 md-small-size-100 margin-top-10">
      <md-card-header>
        <p class="md-title">Users</p>
      </md-card-header>

      <md-card-content>
        <form>
          <md-table class="md-table condensed">
            <md-table-row>
              <md-table-head>First name</md-table-head>
              <md-table-head>Last name</md-table-head>
              <md-table-head>Kind</md-table-head>
              <md-table-head>Active?</md-table-head>
              <md-table-head>E-mail</md-table-head>
              <md-table-head>Actions</md-table-head>
            </md-table-row>

            <md-table-row v-for="user in users" :key="user.id">
              <md-table-cell>
                <span v-if="editingUser != user.id" :title="user.id">{{ user.firstName }}</span>
                <md-field name="firstName" v-if="editingUser == user.id">
                  <md-input name="lastName" v-model="user.firstName" />
                </md-field>
              </md-table-cell>
              <md-table-cell>
                <span v-if="editingUser != user.id">{{ user.lastName }}</span>
                <md-field name="lastName" v-if="editingUser == user.id">
                  <md-input name="lastName" v-model="user.lastName" />
                </md-field>
              </md-table-cell>
              <md-table-cell>{{ user.kindName }}</md-table-cell>
              <md-table-cell>{{ user.active ? 'Yes' : 'No' }}</md-table-cell>
              <md-table-cell>
                <span v-if="editingUser != user.id">{{ user.email }}</span>
                <md-field name="email" v-if="editingUser == user.id">
                  <md-input name="email" v-model="user.email" />
                </md-field>
              </md-table-cell>
              <md-table-cell class="no-wrap">
                <md-menu v-if="editingUser != user.id">
                  <md-button class="md-icon-button" md-menu-trigger>
                    <md-icon>more_vert</md-icon>
                  </md-button>

                  <md-menu-content>
                    <md-menu-item
                      @click="transferOwnership(user)"
                      v-if="enterprise.owner.id != user.id">
                      <span>Transfer ownership</span>
                    </md-menu-item>
                    <md-menu-item
                      @click="editUser(user.id)"
                      v-if="editingUser != user.id">
                      <span>Edit</span>
                    </md-menu-item>
                    <md-menu-item v-if="canTurnIntoAdmin(user)"
                      @click="makeAdmin(user)"
                    >
                      <span>Change to administrator</span>
                    </md-menu-item>
                    <md-menu-item v-if="canTurnIntoEnterpriseUser(user)"
                      @click="makeEnterpriseUser(user)"
                    >
                      <span>Change to enterprise user</span>
                    </md-menu-item>
                    <md-menu-item @click="transferDocuments(user)">
                      <span>Transfer documents to another user</span>
                    </md-menu-item>
                    <md-menu-item @click="requestRemoveMfa(user)" v-if="user.smsProtect">
                      <span>Remove SMS 2FA</span>
                    </md-menu-item>
                  </md-menu-content>
                </md-menu>

                <md-button
                  class="md-raised md-primary"
                  @click="saveUser(user)"
                  v-if="editingUser == user.id">
                  Save
                </md-button>
                <md-button
                  class="md-raised"
                  @click="cancelEdit"
                  v-if="editingUser == user.id">
                  Cancel
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>
        </form>
      </md-card-content>
    </md-card>
    <md-dialog-confirm
      :md-active.sync="transferringOwnership"
      v-if="transferringOwnershipTo"
      md-title="Confirm ownership transfer?"
      :md-content="`To: ${transferringOwnershipTo.name}`"
      @md-cancel="cancelTransfer"
      @md-confirm="confirmTransfer"
    />
    <md-dialog :md-active.sync="changingLevel.dialog">
      <md-dialog-title>Make user administrator</md-dialog-title>

      <md-dialog-content v-if="changingLevel.user">
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-size-100 mb-1">
            <p class="md-subheading mb-0">
              {{ $t('messages.enterprise.changeLevel.' + changingLevel.into) }}
            </p>
          </div>
        </div>
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-size-100 mb-1">
            <p class="md-subheading mb-0">{{ $t('fields.user.name') }}</p>
            <span>{{ changingLevel.user.name }}</span>
          </div>
        </div>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button @click="cancelChangeUserLevel">Cancel</md-button>
        <md-button @click="confirmChangeUserLevel" class="md-raised md-primary">Change</md-button>
      </md-dialog-actions>
    </md-dialog>

    <UserDocumentTransfer
      v-if="transferringDocuments.dialog"
      :fromUser="transferringDocuments.fromUser"
      :users="users"
      v-on="$listeners"
      @cancelDocumentTransfer="cancelDocumentTransfer"
      @documentTransferRequested="documentTransferRequested"
    />

    <md-dialog-confirm
      :md-active.sync="removingMfa.dialog"
      v-if="removingMfa.dialog"
      md-title="Confirm removing SMS 2FA?"
      :md-content="`From: ${removingMfa.fromUser.name}`"
      @md-cancel="removingMfa.dialog = false"
      @md-confirm="confirmRemoveMfa"
    />
  </div>
</template>
<script type="ts">
import EnterpriseService from '@/services/enterprise/EnterpriseService';
import User from '@/datatypes/user';
import Enterprise from '@/datatypes/enterprise';
import EnterpriseUpdateUserRequest from '@/datatypes/enterprise/update-user-request';
import camelcaseKeysDeep from 'camelcase-keys-deep';
import UserDocumentTransfer from '@/components/enterprise/user-document-transfer.vue';

export default {
  name: 'enterprise-users',
  props: {
    enterprise: {
      type: Enterprise,
      required: true,
    },
  },
  components: {
    UserDocumentTransfer,
  },
  watch: {
    localEnterprise(to) {
      return this.loadEnterpriseUsers(to);
    },
  },
  data: () => ({
    enterpriseService: new EnterpriseService(),
    users: [],
    editingUser: '',
    transferringOwnership: false,
    transferringOwnershipTo: undefined,
    transferringDocuments: {
      dialog: false,
      fromUser: undefined,
    },
    changingLevel: {
      dialog: false,
      user: undefined,
    },
    removingMfa: {
      dialog: false,
      fromUser: undefined,
    },
  }),
  methods: {
    loadEnterpriseUsers(enterprise) {
      const service = this.enterpriseService.getEnterpriseUsers(enterprise.id);

      service.then((response) => {
        this.users = response.data.map((data) => new User(camelcaseKeysDeep(data)));
        this.$emit('usersLoaded', this.users);
      });

      return service;
    },
    editUser(id) {
      this.editingUser = id;
    },
    saveUser(user) {
      this.enterpriseService.updateEnterpriseUser(
        this.enterprise.id, user.id, new EnterpriseUpdateUserRequest(user),
      ).then(() => {
        this.$emit('statusMessage', 'Enterprise user saved.');
        this.editingUser = '';
      }).catch((error) => {
        this.$emit('statusMessage', error.response.data.message);
      });
    },
    cancelEdit() {
      this.loadEnterpriseUsers(this.enterprise).then(() => {
        this.editingUser = '';
      });
    },
    transferOwnership(user) {
      this.transferringOwnership = true;
      this.transferringOwnershipTo = user;
    },
    cancelTransfer() {
      this.transferringOwnership = false;
      this.transferringOwnershipTo = undefined;
    },
    confirmTransfer() {
      this.enterpriseService.transferOwnership(
        this.enterprise.id, this.transferringOwnershipTo.id,
      ).then(() => {
        this.$emit('enterpriseReload', () => {
          this.$emit('statusMessage', 'Enterprise ownership transferred.');
        });
      });
    },
    confirmRemoveMfa() {
      this.enterpriseService.removeUserMfa(this.removingMfa.fromUser).then(() => {
        this.$emit('enterpriseReload', () => {
          this.$emit('statusMessage', 'User MFA disabled.');
        });
      });
    },
    confirmChangeUserLevel() {
      const kinds = {
        admin: this.enterprise.owner.kind,
        user: 4,
      };

      const level = kinds[this.changingLevel.into];

      this.enterpriseService.changeUserLevel(
        this.$route.params.id,
        this.changingLevel.user.id,
        { kind: level },
      ).then(
        () => {
          this.$emit('statusMessage', 'User level changed.');
          this.$emit('enterpriseReload');
        },
      ).catch(() => {
        this.$emit('statusMessage', 'Error setting user level.');
      }).finally(() => {
        this.changingLevel = {
          dialog: false,
          user: undefined,
        };
      });
    },
    cancelChangeUserLevel() {
      this.changingLevel = {
        dialog: false,
        user: undefined,
      };
    },
    makeAdmin(user) {
      this.changingLevel = {
        dialog: true,
        into: 'admin',
        user,
      };
    },
    makeEnterpriseUser(user) {
      this.changingLevel = {
        dialog: true,
        into: 'user',
        user,
      };
    },
    canTurnIntoAdmin(user) {
      return ((this.enterprise.owner.id !== user.id) && (this.enterprise.owner.kind !== user.kind));
    },
    canTurnIntoEnterpriseUser(user) {
      return (
        (this.enterprise.owner.id !== user.id) && (user.kind !== 4)
      );
    },
    resetDocumentTransfer() {
      this.transferringDocuments = {
        dialog: false,
        fromUser: undefined,
      };
    },
    cancelDocumentTransfer() {
      this.resetDocumentTransfer();
    },
    documentTransferRequested() {
      this.resetDocumentTransfer();
      this.$emit('statusMessage', 'Document transfer requested.');
    },
    transferDocuments(user) {
      this.transferringDocuments = {
        dialog: true,
        fromUser: user,
      };
    },
    requestRemoveMfa(user) {
      this.removingMfa = {
        dialog: true,
        fromUser: user,
      };
    },
  },
  computed: {
    localEnterprise: {
      get() {
        return this.enterprise;
      },
      set(value) {
        this.$emit('enterpriseChange', value);
      },
    },
  },
};
</script>
